import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Alert,
  Stack,
  Skeleton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  FormHelperText,
  Chip,
} from "@mui/material";
import {
  Event as EventIcon,
  ArrowBack as BackIcon,
  Groups as GroupsIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import api from "../../api";
import GroupSelect from "../../components/GroupSelect";
import { useHostableSubjects } from "../../components/hostableSubjects";
import {
  nextOccurrences,
  slotForSubject,
  slotLabel,
  toLocalInputValue,
} from "../../components/subjectTimeSlots";

const defaultStart = () => {
  const d = new Date();
  d.setMinutes(0, 0, 0);
  d.setHours(d.getHours() + 1);
  return toLocalInputValue(d);
};

const addHours = (localValue, hours) => {
  const d = new Date(localValue);
  if (Number.isNaN(d.getTime())) return "";
  d.setHours(d.getHours() + hours);
  return toLocalInputValue(d);
};

/**
 * Schedule a live class for one group.
 *
 * Only a group's **lead tutor** may host, so the group list is filtered on `can_host_class` —
 * the server enforces the same rule in ClassroomController::store(), this just avoids offering
 * a choice that would be refused. Scheduling notifies the group's students and their parents.
 */
export default function TutorScheduleClass() {
  const navigate = useNavigate();

  const [groups, setGroups] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const [form, setForm] = useState({
    course_plan_id: "",
    subject_id: "",
    name: "",
    capacity: "",
    start_time: defaultStart(),
    end_time: addHours(defaultStart(), 1),
  });

  const hostable = useMemo(
    () => groups.filter((g) => g.can_host_class && g.is_active),
    [groups]
  );

  const selectedGroup = useMemo(
    // GroupSelect hands back a string id; g.id is a number, so compare as strings.
    () => hostable.find((g) => String(g.id) === String(form.course_plan_id)) || null,
    [hostable, form.course_plan_id]
  );

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await api.get("tutor/groups");
      setGroups(Array.isArray(res.data?.data) ? res.data.data : []);
    } catch (e) {
      setError(e?.response?.data?.message || "Could not load your groups.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // Subjects belong to the group's course, so they reload whenever the group changes.
  useEffect(() => {
    const courseId = selectedGroup?.course?.id;
    if (!courseId) {
      setSubjects([]);
      return undefined;
    }
    let cancelled = false;
    api
      .get(`tutor/courses/${courseId}/subjects`)
      .then((res) => {
        if (cancelled) return;
        const list = Array.isArray(res.data?.data) ? res.data.data : [];
        setSubjects(list);
        // useHostableSubjects seeds the selection from the filtered list, not this one.
        setForm((f) => ({ ...f, subject_id: "" }));
      })
      .catch(() => {
        if (!cancelled) setSubjects([]);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedGroup?.course?.id]);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  // Keep the end after the start rather than letting the server reject it.
  const onStartChange = (e) => {
    const start = e.target.value;
    setForm((f) => ({
      ...f,
      start_time: start,
      end_time: !f.end_time || new Date(f.end_time) <= new Date(start) ? addHours(start, 1) : f.end_time,
    }));
  };

  const canSubmit =
    form.course_plan_id && form.subject_id && form.name.trim() && form.start_time && !saving;

  // Where the group is split by subject, only the subjects assigned to this tutor there.
  const hostableSubjects = useHostableSubjects(
    subjects,
    selectedGroup,
    form.subject_id,
    (id) => setForm((f) => ({ ...f, subject_id: id }))
  );

  // The weekly slot an admin pinned this subject to on the group's Staff tab, if any. Most classes
  // run at the same hour every week, so offering the next few of them beats retyping the clock —
  // but the slot is a suggestion, not a constraint: the fields below stay editable, and a subject
  // with no slot behaves exactly as this form always has.
  const slot = useMemo(
    () => slotForSubject(selectedGroup, form.subject_id),
    [selectedGroup, form.subject_id]
  );

  const occurrences = useMemo(
    () => nextOccurrences(slot, { start_date: selectedGroup?.start_date, end_date: selectedGroup?.end_date }),
    [slot, selectedGroup?.start_date, selectedGroup?.end_date]
  );

  const pickOccurrence = (occ) =>
    setForm((f) => ({ ...f, start_time: occ.start, end_time: occ.end || addHours(occ.start, 1) }));

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSaving(true);
    try {
      const res = await api.post("tutor/classrooms", {
        course_id: selectedGroup?.course?.id,
        course_plan_id: Number(form.course_plan_id),
        subject_id: Number(form.subject_id),
        name: form.name.trim(),
        capacity: form.capacity ? Number(form.capacity) : undefined,
        start_time: form.start_time.replace("T", " ") + ":00",
        end_time: form.end_time ? form.end_time.replace("T", " ") + ":00" : undefined,
      });
      toast.success(
        res.data?.message || "Class scheduled. The group's students have been notified."
      );
      navigate("/tutor/classes");
    } catch (err) {
      // 422s from ClassroomController carry the actionable message (capacity, ended plan, not lead).
      const data = err?.response?.data;
      toast.error(
        data?.errors?.course_plan_id?.[0] ||
          data?.errors?.capacity?.[0] ||
          data?.message ||
          "Could not schedule the class."
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Button startIcon={<BackIcon />} onClick={() => navigate("/tutor/classes")} sx={{ mb: 2 }}>
        Back to classes
      </Button>

      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
        <EventIcon color="primary" />
        <Typography variant="h5" fontWeight={700}>
          Schedule a class
        </Typography>
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        The class is visible only to this group's students, who are notified as soon as you
        schedule it — and again shortly before it starts.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Skeleton variant="rounded" height={320} />
      ) : hostable.length === 0 ? (
        <Alert severity="info">
          You are not the lead tutor of any active group. Only a group's lead tutor can host its
          classes — a tutor assistant cannot.
        </Alert>
      ) : (
        <Card variant="outlined" sx={{ borderRadius: 2, maxWidth: 720 }}>
          <CardContent>
            <form onSubmit={submit}>
              <Stack spacing={2.5}>
                {/* This page is group-only — a class with no group belongs on the Classes page,
                    which offers the whole-course option explicitly. */}
                <GroupSelect
                  value={form.course_plan_id}
                  onChange={(id) => setForm((f) => ({ ...f, course_plan_id: id, subject_id: "" }))}
                  allowWholeCourse={false}
                  helperText="Only groups you lead are listed."
                />

                {selectedGroup && (
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    <Chip
                      size="small"
                      icon={<GroupsIcon />}
                      label={`${selectedGroup.student_count ?? 0} student${
                        selectedGroup.student_count === 1 ? "" : "s"
                      }`}
                    />
                    {selectedGroup.mode && <Chip size="small" variant="outlined" label={selectedGroup.mode} />}
                    {/* An online group usually carries "online" as its location too — showing it
                        twice reads as a bug, so only add it when it says something new. */}
                    {selectedGroup.location &&
                      selectedGroup.location.trim().toLowerCase() !==
                        String(selectedGroup.mode ?? "").trim().toLowerCase() && (
                        <Chip size="small" variant="outlined" label={selectedGroup.location} />
                      )}
                  </Stack>
                )}

                <FormControl fullWidth size="small" disabled={!form.course_plan_id}>
                  <InputLabel>Subject</InputLabel>
                  <Select value={form.subject_id} label="Subject" onChange={set("subject_id")}>
                    {hostableSubjects.map((s) => (
                      <MenuItem key={s.id} value={s.id}>
                        {s.name}
                      </MenuItem>
                    ))}
                  </Select>
                  {form.course_plan_id && hostableSubjects.length === 0 && (
                    <FormHelperText>
                      You are not assigned to any subject on this group's course.
                    </FormHelperText>
                  )}
                </FormControl>

                <TextField
                  fullWidth
                  size="small"
                  required
                  label="Class name"
                  value={form.name}
                  onChange={set("name")}
                  placeholder="e.g. Week 5 — Comprehension"
                />

                {slot && (
                  <Box>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.75 }}>
                      This subject runs <strong>{slotLabel(slot)}</strong>
                      {slot.location_name ? ` at ${slot.location_name}` : ""}.{" "}
                      {occurrences.length > 0
                        ? "Pick a date to fill in the times below, or set them yourself."
                        : "No upcoming dates left in this group's run — set the times yourself below."}
                    </Typography>
                    <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                      {occurrences.map((occ) => (
                        <Chip
                          key={occ.key}
                          size="small"
                          clickable
                          variant={form.start_time === occ.start ? "filled" : "outlined"}
                          color={form.start_time === occ.start ? "primary" : "default"}
                          label={`${occ.label} · ${slot.start_time}`}
                          onClick={() => pickOccurrence(occ)}
                        />
                      ))}
                    </Stack>
                  </Box>
                )}

                <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                  <TextField
                    fullWidth
                    size="small"
                    required
                    type="datetime-local"
                    label="Starts"
                    InputLabelProps={{ shrink: true }}
                    value={form.start_time}
                    onChange={onStartChange}
                  />
                  <TextField
                    fullWidth
                    size="small"
                    type="datetime-local"
                    label="Ends"
                    InputLabelProps={{ shrink: true }}
                    value={form.end_time}
                    onChange={set("end_time")}
                  />
                </Stack>

                <TextField
                  size="small"
                  type="number"
                  label="Capacity (optional)"
                  value={form.capacity}
                  onChange={set("capacity")}
                  helperText={
                    selectedGroup
                      ? `Leave blank for no limit. This group has ${selectedGroup.student_count ?? 0} students.`
                      : "Leave blank for no limit."
                  }
                  inputProps={{ min: 1 }}
                  sx={{ maxWidth: 260 }}
                />

                <Stack direction="row" spacing={1} justifyContent="flex-end">
                  <Button onClick={() => navigate("/tutor/classes")} disabled={saving}>
                    Cancel
                  </Button>
                  <Button type="submit" variant="contained" disabled={!canSubmit}>
                    {saving ? "Scheduling…" : "Schedule class"}
                  </Button>
                </Stack>
              </Stack>
            </form>
          </CardContent>
        </Card>
      )}
    </Box>
  );
}
